import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAuth } from "../../auth/authSlice";
import { RoleTypes } from "../../auth/types/roleTypes";
import { UserTypes } from "../../auth/types/authType";
import { createWallPost, fetchWallPosts, selectWall } from "../../wall/wallSlices";
import { fetchOfficeUsers } from "../../wall/wallSearch";
import WallPostCard from "../../wall/components/WallPostCard";
import { WallCategory, WallUser } from "../../wall/types/wallTypes";
import { Complaint } from "../types/complaintTypes";

type Props = {
  complaint: Complaint;
};

export default function ComplaintFollowUp({ complaint }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { posts } = useAppSelector(selectWall);

  const [officeUsers, setOfficeUsers] = useState<WallUser[]>([]);
  const [message, setMessage] = useState("");
  const [mentions, setMentions] = useState<WallUser[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const roleNames = (user?.roles ?? []).map((r) => r.displayName);
  const isSuper = user?.userType === UserTypes.Super;
  const canModerate = isSuper || roleNames.includes(RoleTypes.ModerateWall);

  const complaintId = complaint._id;
  const historyLen = complaint.statusHistory?.length ?? 0;

  useEffect(() => {
    fetchOfficeUsers().then(setOfficeUsers);
  }, []);

  useEffect(() => {
    if (complaintId) {
      dispatch(fetchWallPosts({ skip: 0, take: 50, complaintId }));
    }
  }, [dispatch, complaintId, historyLen]);

  const handlePost = async () => {
    if (!message.trim() || !complaintId) return;
    setSubmitting(true);
    await dispatch(
      createWallPost({
        message: message.trim(),
        category: WallCategory.ComplaintFollowUp,
        complaintId,
        employeeIds: complaint.employee?._id ? [complaint.employee._id] : [],
        mentionIds: mentions.map((m) => m._id!).filter(Boolean),
      }),
    );
    setMessage("");
    setMentions([]);
    setSubmitting(false);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t("followUp")}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label={t("writeFollowUp")}
          placeholder={t("writeFollowUp")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Autocomplete
            multiple
            options={officeUsers}
            value={mentions}
            getOptionLabel={(o) => (o as WallUser).name ?? ""}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            onChange={(_, v) => setMentions(v as WallUser[])}
            sx={{ flex: 1, minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} size="small" label={t("mention")} />
            )}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Button
              variant="contained"
              onClick={handlePost}
              disabled={submitting || !message.trim()}
            >
              {t("postFollowUp")}
            </Button>
          </Box>
        </Stack>
      </Stack>

      {posts.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t("noPostsYet")}
        </Typography>
      ) : (
        posts.map((post) => (
          <WallPostCard
            key={post._id}
            post={post}
            currentUserId={user?._id}
            canModerate={canModerate}
          />
        ))
      )}
    </Box>
  );
}
